import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  VStack,
  FormErrorMessage,
  Flex,
  RadioGroup,
  Radio,
  Spinner,
  InputGroup,
  InputRightElement,
  IconButton,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSubscriber } from "@root/hooks/useSubscriber";
import { NewsPaperType, ORG_TYPE, SUBSCRIBER_TYPE } from "@root/constants";
import { useOrganizationBranding } from "@root/context/organization-branding-context";

interface AddSubscriberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** When provided the modal operates in edit mode with pre-filled fields */
  subscriber?: any;
}

export const AddSubscriberModal: React.FC<AddSubscriberModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  subscriber,
}) => {
  const { t } = useTranslation("common");
  const {
    createSubscriber,
    createSubscribersLoading,
    updateSubscriberById,
    updateSubscribersLoading,
  } = useSubscriber();
  const { slug } = useOrganizationBranding();

  const isEditMode = !!subscriber;

  // Form state
  const [company, setCompany] = useState("");
  const [adminName, setAdminName] = useState("");
  const [emailOrId, setEmailOrId] = useState("");
  const [type, setType] = useState<string>(String(SUBSCRIBER_TYPE.SUBSCRIBER));
  const [newpaperType, setnewpaperType] = useState<string>(
    String(NewsPaperType.Daily),
  );
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Pre-fill fields when switching to edit mode or when subscriber changes
  useEffect(() => {
    if (isEditMode && isOpen) {
      setCompany(subscriber.companyName ?? "");
      setAdminName(subscriber.fullName ?? "");
      setEmailOrId(subscriber.email ?? subscriber.loginId ?? "");
      setType(String(subscriber.role ?? SUBSCRIBER_TYPE.SUBSCRIBER));
      setnewpaperType(String(subscriber.newsPaperType ?? NewsPaperType.Daily));
      setPassword("");
      setConfirmPassword("");
    }
  }, [subscriber, isOpen]);

  // Validation — password rules mirror the BE (UpdateOrganizationMembershipDto):
  //   • optional (blank = keep current password)
  //   • max 256 characters when provided
  const PASSWORD_MAX_LENGTH = 256;
  const isEmailOrIdValid = emailOrId.trim().length > 0;
  const isPasswordTooLong = isEditMode && password.length > PASSWORD_MAX_LENGTH;
  const isPasswordValid = !isEditMode || password === "" || !isPasswordTooLong;
  const isPasswordMismatch =
    isEditMode && !!confirmPassword && password !== confirmPassword;
  const isConfirmPasswordValid =
    !isEditMode || password === "" || password === confirmPassword;

  const isFormValid =
    !!company.trim() &&
    !!adminName.trim() &&
    isEmailOrIdValid &&
    isPasswordValid &&
    isConfirmPasswordValid;

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (!isFormValid) return;

    try {
      if (isEditMode) {
        const updateData: Record<string, any> = {
          fullName: adminName.trim(),
          companyName: company.trim(),
          newsPaperType: Number(newpaperType),
          organizationType:
            type === String(SUBSCRIBER_TYPE.SUBSCRIBER)
              ? ORG_TYPE.SUBSCRIPTION
              : ORG_TYPE.NON_SUBSCRIPTION,
        };
        if (password) {
          updateData.password = password;
        }
        await updateSubscriberById(subscriber.id, updateData);
      } else {
        const identifier = emailOrId.trim();
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
        const subscriberData: Record<string, any> = {
          fullName: adminName.trim(),
          companyName: company.trim(),
          role: Number(type),
          slug: slug ?? "",
          organizationType:
            type === String(SUBSCRIBER_TYPE.SUBSCRIBER)
              ? ORG_TYPE.SUBSCRIPTION
              : ORG_TYPE.NON_SUBSCRIPTION,
          newsPaperType: Number(newpaperType),
          ...(isEmail ? { email: identifier } : { loginId: identifier }),
        };
        await createSubscriber(subscriberData);
      }
      resetForm();
      onSuccess?.();
    } catch (e) {
      console.error("Error saving subscriber:", e);
      setIsSubmitted(false);
    }
  };

  const resetForm = () => {
    setCompany("");
    setAdminName("");
    setEmailOrId("");
    setType(String(SUBSCRIBER_TYPE.SUBSCRIBER));
    setnewpaperType(String(NewsPaperType.Daily));
    setPassword("");
    setConfirmPassword("");
    setIsSubmitted(false);
    onClose();
  };

  const isLoading = isEditMode
    ? updateSubscribersLoading
    : createSubscribersLoading;

  return (
    <Modal isOpen={isOpen} onClose={resetForm} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p="32px" bg="#FFFFFF" minWidth="832px">
        <ModalHeader as="h4" fontSize="24px" fontWeight="bold" color="#2F2F34">
          {isEditMode
            ? t("subscriberManagement.editSubscriber", "Edit Subscriber")
            : t("subscriberManagement.addSubscriber")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing="24px">
            {/* Company Name */}
            <FormControl isInvalid={isSubmitted && !company.trim()}>
              <FormLabel fontSize="16px" fontWeight="bold">
                {t("subscriberManagement.subscriberCompanyName")}
              </FormLabel>
              <Input
                placeholder={t("subscriberManagement.enterCompanyName")}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
              <FormErrorMessage>
                {t("subscriberManagement.requiredField")}
              </FormErrorMessage>
            </FormControl>

            {/* Admin Name */}
            <FormControl isInvalid={isSubmitted && !adminName.trim()}>
              <FormLabel fontSize="16px" fontWeight="bold">
                {t("subscriberManagement.subscriberTittle")}
              </FormLabel>
              <Input
                placeholder={t("subscriberManagement.enterAdminName")}
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
              />
              <FormErrorMessage>
                {t("subscriberManagement.requiredField")}
              </FormErrorMessage>
            </FormControl>

            {/* Email / ID — disabled in edit mode */}
            <FormControl isInvalid={isSubmitted && !emailOrId.trim()}>
              <FormLabel fontSize="16px" fontWeight="bold">
                {t("subscriberManagement.emailOrId", "Email / ID")}
              </FormLabel>
              <Input
                placeholder={t(
                  "subscriberManagement.enterEmailOrId",
                  "Enter email or login ID (e.g. user@example.com or kpec_kado)",
                )}
                value={emailOrId}
                onChange={(e) => setEmailOrId(e.target.value)}
                isDisabled={isEditMode}
                bg={isEditMode ? "gray.50" : undefined}
                _disabled={{ opacity: 0.7, cursor: "not-allowed" }}
              />
              <FormErrorMessage>
                {t("subscriberManagement.requiredField")}
              </FormErrorMessage>
            </FormControl>

            {/* Subscriber Type radio */}
            <FormControl>
              <FormLabel fontSize="16px" fontWeight="bold">
                {t("subscriberManagement.type")}
              </FormLabel>
              <RadioGroup value={type} onChange={setType}>
                <Flex
                  gap={4}
                  justifyContent="space-between"
                  color="#2F2F34"
                  fontSize="14px"
                >
                  <Radio value="3">{t("signup.subscriber")}</Radio>
                  <Radio value="4" mr="20%">
                    {t("signup.nonSubscriber")}
                  </Radio>
                </Flex>
              </RadioGroup>
            </FormControl>

            {/* Newspaper Type */}
            <FormControl>
              <FormLabel fontSize="16px" fontWeight="bold">
                {t("subscriberManagement.subscriberType")}
              </FormLabel>
              <RadioGroup value={newpaperType} onChange={setnewpaperType}>
                <Flex
                  gap={4}
                  justifyContent="space-between"
                  color="#2F2F34"
                  fontSize="14px"
                >
                  <Radio value="1">{t("subscriberManagement.daily")}</Radio>
                  <Radio value="2" mr="20%">
                    {t("subscriberManagement.weekly")}
                  </Radio>
                  <Radio value="3" mr="20%">
                    {t("subscriberManagement.other")}
                  </Radio>
                </Flex>
              </RadioGroup>
            </FormControl>

            {/* Password fields — only shown in edit mode */}
            {isEditMode && (
              <>
                <FormControl isInvalid={isPasswordTooLong}>
                  <FormLabel fontSize="16px" fontWeight="bold">
                    {t("subscriberManagement.newPassword", "New Password")}
                    <span
                      style={{
                        fontWeight: 400,
                        fontSize: "13px",
                        color: "#798692",
                        marginLeft: "8px",
                      }}
                    >
                      {/* {t("subscriberManagement.passwordOptional")} */}
                    </span>
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t(
                        "subscriberManagement.enterNewPassword",
                        "Enter new password",
                      )}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      maxLength={PASSWORD_MAX_LENGTH}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword((v) => !v)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {t(
                      "subscriberManagement.passwordTooLong",
                      `Password must not exceed ${PASSWORD_MAX_LENGTH} characters`,
                    )}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={isPasswordMismatch}>
                  <FormLabel fontSize="16px" fontWeight="bold">
                    {t(
                      "subscriberManagement.confirmPassword",
                      "Confirm Password",
                    )}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t(
                        "subscriberManagement.enterConfirmPassword",
                      )}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        icon={
                          showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                        }
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {t(
                      "subscriberManagement.passwordMismatch",
                      "Passwords do not match",
                    )}
                  </FormErrorMessage>
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap="16px">
          <Button
            onClick={resetForm}
            variant="outline"
            color="#1819B4"
            border="1px"
            borderColor="#1819B4"
          >
            {t("subscriberManagement.cancel")}
          </Button>
          <Button
            bg="#1819B4"
            colorScheme="blue"
            onClick={handleSubmit}
            isDisabled={isEditMode ? !isFormValid : !isFormValid && isSubmitted}
            isLoading={isLoading}
          >
            {isLoading ? (
              <Spinner size="sm" />
            ) : isEditMode ? (
              t("subscriberManagement.saveChanges", "Save Changes")
            ) : (
              t("subscriberManagement.confirmAddSubscriber")
            )}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
